import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
/* eslint-disable no-console */
import { useEffect, useRef, useState } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { DEFAULT_DELAY_MS, DEFAULT_RETRIES, DEFAULT_RETRY_TIMEOUT_MS } from './RecaptchaProvider.constants';
import { RecaptchaLoadedProxy, cleanGoogleRecaptcha, getRecaptchaFunction, getRecaptchaInstance, getSiteKey, injectRecaptchaScript, isRecaptchaInjected } from './RecaptchaProvider.utils';
import useUserInteraction from '../../modules/foundation/hooks/use-user-interaction/use-user-interaction';
import retry from '../../modules/foundation/services/retry';
var initialized = false;
var scriptLoadingStarted = false;
export function useRecaptcha() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$delay = _ref.delay,
    delay = _ref$delay === void 0 ? DEFAULT_DELAY_MS : _ref$delay,
    _ref$attempts = _ref.attempts,
    attempts = _ref$attempts === void 0 ? DEFAULT_RETRIES : _ref$attempts,
    _ref$retryTimeout = _ref.retryTimeout,
    retryTimeout = _ref$retryTimeout === void 0 ? DEFAULT_RETRY_TIMEOUT_MS : _ref$retryTimeout;
  var activeProviderRef = useRef(false);
  var activeProvider = activeProviderRef.current;
  var proxy = new RecaptchaLoadedProxy();
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    attempt = _useState2[0],
    setAttempt = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    loaded = _useState4[0],
    setLoaded = _useState4[1];
  var siteKey = getSiteKey();
  var siteKeyAvailable = !!siteKey;
  if (!initialized && siteKeyAvailable) {
    // this guarantee only one attempts to load the script
    initialized = true;
    activeProviderRef.current = true;
  }
  function setRecaptchaAsLoaded() {
    var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      onLoad = _ref2.onLoad;
    if (loaded) {
      return;
    }
    var grecaptcha = getRecaptchaInstance();
    if (grecaptcha) {
      grecaptcha.ready(function () {
        setLoaded(true);
        if (activeProvider) {
          proxy.setLoaded();
        }
        if (onLoad) {
          onLoad();
        }
      });
    }
  }
  function loadRecaptcha() {
    var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _onLoad = _ref3.onLoad,
      _ref3$force = _ref3.force,
      force = _ref3$force === void 0 ? false : _ref3$force;
    if (!activeProvider || loaded) {
      return;
    }
    if (!force && scriptLoadingStarted) {
      return;
    }
    scriptLoadingStarted = true;
    injectRecaptchaScript({
      onLoad: function onLoad() {
        setRecaptchaAsLoaded({
          onLoad: _onLoad
        });
      }
    });
  }
  var _useUserInteraction = useUserInteraction(),
    anyInteraction = _useUserInteraction.anyInteraction;
  useEffect(function () {
    if (anyInteraction) {
      loadRecaptcha();
    }
  }, [anyInteraction]);
  useEffect(function () {
    var timeoutId;
    if (activeProvider && !loaded) {
      timeoutId = setTimeout(function () {
        loadRecaptcha({
          force: true
        });
      }, delay);
    }
    if (!activeProvider) {
      proxy.registerCallback(function () {
        // set internal loaded to true when the active provider loads the script
        setRecaptchaAsLoaded();
      });
    }
    if (isRecaptchaInjected()) {
      setRecaptchaAsLoaded();
    }
    return function () {
      clearTimeout(timeoutId);
    };
  }, [activeProvider]);
  useEffect(function () {
    var timeoutId = setTimeout(function () {
      if (!siteKey || isRecaptchaInjected()) {
        if (activeProvider) {
          console.log("Recaptcha loaded after ".concat(attempt, " extra attempts!"));
        }
        setRecaptchaAsLoaded();
        clearTimeout(timeoutId);
        return;
      }
      if (activeProvider) {
        if (attempt >= attempts || attempts === 0) {
          console.error("Couldn't load Recaptcha after ".concat(attempts, " attempts"));
          clearTimeout(timeoutId);
        } else {
          console.warn("Retrying Recaptcha load attempt ".concat(attempt + 1, "..."));
          cleanGoogleRecaptcha();
          loadRecaptcha({
            force: true
          });
          setAttempt(function (currentAttempt) {
            return currentAttempt + 1;
          });
        }
      }
    }, attempt === 0 ? delay / 2 + retryTimeout : retryTimeout);
    return function () {
      clearTimeout(timeoutId);
    };
  }, [attempt, activeProvider]);
  var getToken = /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3(action) {
      var recaptchaFunction, token;
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) switch (_context3.prev = _context3.next) {
          case 0:
            recaptchaFunction = getRecaptchaFunction(siteKey);
            if (siteKeyAvailable) {
              _context3.next = 3;
              break;
            }
            return _context3.abrupt("return", null);
          case 3:
            if (loaded) {
              _context3.next = 5;
              break;
            }
            return _context3.abrupt("return", null);
          case 5:
            if (recaptchaFunction) {
              _context3.next = 8;
              break;
            }
            console.error('recaptchaFunction is not defined and should be');
            return _context3.abrupt("return", null);
          case 8:
            _context3.prev = 8;
            _context3.next = 11;
            return retry(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
              return _regeneratorRuntime.wrap(function _callee$(_context) {
                while (1) switch (_context.prev = _context.next) {
                  case 0:
                    return _context.abrupt("return", recaptchaFunction(action));
                  case 1:
                  case "end":
                    return _context.stop();
                }
              }, _callee);
            })), DEFAULT_RETRIES);
          case 11:
            token = _context3.sent;
            if (_isEmpty(token)) {
              // retry if google returned an empty token
              token = retry(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
                return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                  while (1) switch (_context2.prev = _context2.next) {
                    case 0:
                      return _context2.abrupt("return", recaptchaFunction(action));
                    case 1:
                    case "end":
                      return _context2.stop();
                  }
                }, _callee2);
              })), DEFAULT_RETRIES);
            }
            return _context3.abrupt("return", token);
          case 16:
            _context3.prev = 16;
            _context3.t0 = _context3["catch"](8);
            // eslint-disable-next-line no-console
            console.error(_context3.t0);
            return _context3.abrupt("return", null);
          case 20:
          case "end":
            return _context3.stop();
        }
      }, _callee3, null, [[8, 16]]);
    }));
    return function getToken(_x) {
      return _ref4.apply(this, arguments);
    };
  }();
  var finalLoaded = !siteKeyAvailable ? true : loaded;
  return [getToken, finalLoaded, {
    loadRecaptcha: loadRecaptcha
  }];
}